import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Film, Loader2, Trash2, Upload, ImageIcon, X, ChevronLeft, Clapperboard,
  FolderPlus, Scissors, ArrowUp, ArrowLeft, ArrowRight, FolderOpen, Save, Play, RotateCcw, User, Mountain,
} from "lucide-react";
import { projectsApi, pipelinesApi, promptsApi, streamAsset } from "@/api/mist";
import { StitchingStudio } from "@/components/mist/StitchingStudio";
import VaultPickButton from "@/components/mist/VaultPickButton";
import SaveDestinations from "@/components/gallery/SaveDestinations";

const GOLD = "#FFD028";
const DURS = [6, 8, 10];

// Pre-coded CHARACTER MOVEMENT macros (real locomotion — explicitly NOT pan/zoom).
const MOTION_MACROS = [
  { k: "forward", label: "Forward March", Icon: ArrowUp, prompt: "The hero character runs forward down the lane toward the top of the screen in a steady run cycle, legs and arms animating naturally, the squad of troops following close behind in tight formation, continuous forward locomotion. The camera stays STATIC and locked — absolutely no camera pan, zoom, dolly or tilt; only the characters move." },
  { k: "strafe_l", label: "Strafe Left", Icon: ArrowLeft, prompt: "The hero character quickly strafes to the LEFT across the lane, sidestepping left while still facing forward, weapon held ready, a sharp lateral dodge with feet shuffling, the troops mirroring the sidestep. The camera stays STATIC and locked — no camera pan, zoom or tilt; only the characters move." },
  { k: "strafe_r", label: "Strafe Right", Icon: ArrowRight, prompt: "The hero character quickly strafes to the RIGHT across the lane, sidestepping right while still facing forward, weapon held ready, a sharp lateral dodge with feet shuffling, the troops mirroring the sidestep. The camera stays STATIC and locked — no camera pan, zoom or tilt; only the characters move." },
];

/* Streamed silent-clip preview for a scene card. */
function SceneVideo({ assetId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let u; let alive = true;
    if (assetId) streamAsset(assetId).then((x) => { if (alive) { u = x; setSrc(x); } }).catch(() => {});
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [assetId]);
  if (!src) return <div className="w-full h-full grid place-items-center bg-black"><Loader2 size={16} className="text-white/40 animate-spin" /></div>;
  return <video src={src} muted loop playsInline controls preload="metadata" className="w-full h-full object-contain bg-black" />;
}

function SceneCard({ scene, index, onDelete, busy }) {
  const processing = scene.status === "processing";
  const failed = scene.status === "failed";
  return (
    <div data-testid={`scene-card-${scene.id}`} className="border-2 border-black bg-[#0d0d0d] shadow-[3px_3px_0px_#000] overflow-hidden">
      <div className="relative aspect-[9/16] bg-black">
        {processing ? (
          <div className="absolute inset-0 grid place-items-center gap-2" data-testid={`scene-status-${scene.id}`}>
            <Loader2 size={26} className="text-[#FFD028] animate-spin" />
            <span className="font-mono text-[8px] uppercase tracking-widest text-white/50">Rendering…</span>
          </div>
        ) : failed ? (
          <div className="absolute inset-0 grid place-items-center p-2 text-center" data-testid={`scene-status-${scene.id}`}>
            <span className="font-mono text-[8px] uppercase tracking-widest text-red-400">{scene.error || "Failed"}</span>
          </div>
        ) : <SceneVideo assetId={scene.asset_id} />}
        <span className="absolute top-1 left-1 font-mono text-[8px] font-black bg-[#FFD028] text-black px-1.5 py-0.5 border border-black">#{index + 1}</span>
        <span className="absolute top-1 right-1 font-mono text-[8px] font-black bg-black/70 text-white px-1.5 py-0.5 border border-white/20">{scene.duration}s</span>
      </div>
      <div className="p-1.5">
        <p className="font-mono text-[8px] text-white/50 line-clamp-2 min-h-[20px] leading-snug">{scene.motion_prompt || scene.brief || "—"}</p>
        <button onClick={() => onDelete(scene)} disabled={busy || processing} data-testid={`scene-delete-${scene.id}`}
          className="mt-1.5 w-full flex items-center justify-center gap-1 border-2 border-black bg-[#141414] text-red-400 py-1.5 font-mono text-[8px] font-black uppercase tracking-widest active:bg-red-500 active:text-white disabled:opacity-40">
          <Trash2 size={10} /> Delete
        </button>
      </div>
    </div>
  );
}

// One categorised image slot (Character base / Scene ref / Styling ref).
function ImgSlot({ label, sub, Icon, src, onPick, onClear, testid }) {
  const ref = useRef(null);
  const pick = (e) => { const f = (e.target.files || [])[0]; if (e.target) e.target.value = ""; if (!f) return; if (!f.type.startsWith("image/")) { toast.error("Pick an image"); return; } onPick(f); };
  return (
    <div data-testid={testid}>
      <p className="font-mono text-[9px] font-black uppercase tracking-widest text-[#FFD028]">{label}</p>
      <p className="font-mono text-[7px] text-white/40 mb-1">{sub}</p>
      <input ref={ref} type="file" accept="image/*" hidden onChange={pick} data-testid={`${testid}-input`} />
      {src ? (
        <div className="relative aspect-square border-2 border-black bg-black overflow-hidden shadow-[2px_2px_0px_#000]">
          <img src={src} alt={label} className="w-full h-full object-cover" />
          <button onClick={onClear} data-testid={`${testid}-clear`} className="absolute top-0.5 right-0.5 w-6 h-6 grid place-items-center border-2 border-black bg-[#141414] text-white"><X size={11} /></button>
        </div>
      ) : (
        <div className="space-y-1">
          <button onClick={() => ref.current?.click()} data-testid={`${testid}-pick`} className="w-full aspect-square flex flex-col items-center justify-center gap-1 border-2 border-dashed border-white/25 bg-[#141414] text-white/45 active:scale-95">
            <Icon size={20} /><span className="font-mono text-[7px] font-black uppercase tracking-widest">Add</span>
          </button>
          <VaultPickButton onFile={(f) => onPick(f)} bucket="images" testid={`${testid}-vault`} label="Vault"
            className="w-full flex items-center justify-center gap-1 border-2 border-black bg-black text-[#00F5D4] py-1 font-mono text-[7px] font-black uppercase tracking-widest" />
        </div>
      )}
    </div>
  );
}

function ProjectWorkspace({ project, onBack }) {
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showStitch, setShowStitch] = useState(false);

  // Creator state
  const [char, setChar] = useState(null);      // {file, src} — animated base
  const [sceneRef, setSceneRef] = useState(null);
  const [styleRef, setStyleRef] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [forwardOn, setForwardOn] = useState(true);
  const [fps, setFps] = useState("65");
  const [duration, setDuration] = useState(6);
  const [lastAsset, setLastAsset] = useState(null); // {id} of the just-rendered scene

  // Working-prompt album (reuses the Mist Create Prompt Folder API)
  const [folderOpen, setFolderOpen] = useState(false);
  const [saved, setSaved] = useState([]);
  const [folderBusy, setFolderBusy] = useState(false);

  const load = async () => {
    try { const r = await projectsApi.get(project.id); setScenes(r.scenes || []); }
    catch { toast.error("Could not load project"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("mist:director-render", { detail: { active: true } }));
    return () => window.dispatchEvent(new CustomEvent("mist:director-render", { detail: { active: false } }));
  }, []);
  useEffect(() => () => { [char, sceneRef, styleRef].forEach((x) => x?.src && URL.revokeObjectURL(x.src)); /* eslint-disable-next-line */ }, []);

  const mk = (f) => ({ file: f, src: URL.createObjectURL(f) });
  const buildMotion = () => {
    const parts = [];
    if (forwardOn) parts.push(`The hero character marches forward down the road toward the top of the screen in a steady run cycle at ${fps || "65"} fps, troops following in formation, continuous forward locomotion. The camera stays static and locked — no camera pan, zoom or tilt; only the characters move.`);
    if (prompt.trim()) parts.push(prompt.trim());
    if (sceneRef || styleRef) parts.push("Match the environment, setting and art style of the provided reference imagery.");
    return parts.join(" ");
  };

  const generate = async () => {
    if (!char?.file) { toast.error("Add a Character image (slot 1)"); return; }
    setGenerating(true); setLastAsset(null);
    try {
      const r = await projectsApi.addScene(project.id, { mode: "image", motion_prompt: buildMotion(), brief: "", duration, file: char.file });
      await load();
      const j = await pipelinesApi.poll(r.job_id, { timeout: 300000 });
      if (j.status === "completed" && j.asset_id) { toast.success("Scene rendered ✓ — kept in this Project"); setLastAsset({ id: j.asset_id }); }
      else toast.error(j.error || "Scene failed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generate failed"); await load(); }
    finally { setGenerating(false); }
  };

  const clearInputs = () => {
    [char, sceneRef, styleRef].forEach((x) => x?.src && URL.revokeObjectURL(x.src));
    setChar(null); setSceneRef(null); setStyleRef(null); setPrompt(""); setLastAsset(null);
  };
  const deleteScene = async (scene) => {
    setBusy(true);
    try { await projectsApi.deleteScene(project.id, scene.id); setScenes((s) => s.filter((x) => x.id !== scene.id)); }
    catch { toast.error("Delete failed"); }
    finally { setBusy(false); }
  };
  // "Fix last scene": delete the just-rendered clip and keep the inputs so the
  // creator can adjust the prompt/macro and re-generate a replacement.
  const fixLast = async () => {
    const last = [...scenes].reverse().find((s) => s.asset_id === lastAsset?.id);
    if (last) { try { await projectsApi.deleteScene(project.id, last.id); } catch { /* noop */ } await load(); }
    setLastAsset(null);
    toast("Adjust your prompt or macro, then generate again");
  };

  // Working-prompt album
  const loadFolder = async () => {
    setFolderBusy(true);
    try { const r = await promptsApi.list("personal", ""); setSaved(r.prompts || []); }
    catch { toast.error("Couldn't load your prompts"); }
    finally { setFolderBusy(false); }
  };
  const openFolder = () => { setFolderOpen(true); loadFolder(); };
  const saveWorkingPrompt = async () => {
    const text = buildMotion().trim();
    if (!text) { toast.error("Nothing to save yet"); return; }
    try { await promptsApi.save({ name: `${project.title} — scene`, prompt_text: text, scope: "personal", template: false }); toast.success("Saved to your prompt album ✓"); }
    catch { toast.error("Save failed"); }
  };

  const completed = scenes.filter((s) => s.status === "completed");
  const totalSecs = completed.reduce((a, s) => a + (s.duration || 0), 0);
  const creditsHeld = completed.length * 3;

  if (showStitch) return <StitchingStudio project={project} onBack={() => { setShowStitch(false); load(); }} onCompiled={() => {}} />;

  return (
    <div className="h-full w-full flex flex-col overflow-hidden bg-[#141414]" data-testid="project-workspace">
      <div className="shrink-0 flex items-center gap-2 px-4 py-2 border-b-2 border-black bg-[#141414]">
        <button onClick={onBack} data-testid="pv-back" className="w-8 h-8 grid place-items-center border-2 border-black bg-[#141414] shadow-[2px_2px_0px_#000]"><ChevronLeft size={16} /></button>
        <div className="min-w-0 flex-1">
          <p className="font-serif text-lg text-white truncate leading-none" data-testid="pv-project-title">{project.title}</p>
          <p className="font-mono text-[8px] uppercase tracking-widest text-white/40 mt-1">{completed.length} scene{completed.length === 1 ? "" : "s"} · {totalSecs}s</p>
        </div>
        <button onClick={() => setShowStitch(true)} data-testid="pv-open-stitch" className="flex items-center gap-1 border-2 border-black bg-black text-[#FFD028] px-2 py-1.5 font-mono text-[8px] font-black uppercase tracking-widest"><Scissors size={11} /> Join</button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-5">
        <div className="relative border-2 border-black bg-[#0d0d0d] shadow-[4px_4px_0px_#000] p-3 space-y-4" data-testid="pv-creator">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 font-mono text-[10px] font-black uppercase tracking-widest text-white"><Clapperboard size={15} className="text-[#FFD028]" /> New Scene</span>
            <button onClick={openFolder} data-testid="pv-folder-open" className="flex items-center gap-1 border-2 border-black bg-[#141414] text-[#FFD028] px-2 py-1 font-mono text-[8px] font-black uppercase tracking-widest"><FolderOpen size={11} /> Prompt Album</button>
          </div>

          {/* 1 · Three target images */}
          <div className="grid grid-cols-3 gap-2" data-testid="pv-image-slots">
            <ImgSlot label="Character" sub="Animated base" Icon={User} testid="pv-slot-character" src={char?.src} onPick={(f) => setChar(mk(f))} onClear={() => { char?.src && URL.revokeObjectURL(char.src); setChar(null); }} />
            <ImgSlot label="Scene" sub="Setting ref" Icon={Mountain} testid="pv-slot-scene" src={sceneRef?.src} onPick={(f) => setSceneRef(mk(f))} onClear={() => { sceneRef?.src && URL.revokeObjectURL(sceneRef.src); setSceneRef(null); }} />
            <ImgSlot label="Styling" sub="Look ref" Icon={ImageIcon} testid="pv-slot-styling" src={styleRef?.src} onPick={(f) => setStyleRef(mk(f))} onClear={() => { styleRef?.src && URL.revokeObjectURL(styleRef.src); setStyleRef(null); }} />
          </div>

          {/* 2 · Movement — forward toggle opens an FPS input */}
          <div>
            <p className="font-mono text-[9px] font-black uppercase tracking-widest text-[#FFD028] mb-1.5">Movement</p>
            <button onClick={() => setForwardOn((v) => !v)} data-testid="pv-forward-toggle"
              className={`w-full flex items-center justify-center gap-2 border-2 border-black py-2.5 font-mono text-[9px] font-black uppercase tracking-widest shadow-[2px_2px_0px_#000] ${forwardOn ? "bg-[#FFD028] text-black" : "bg-[#141414] text-white/60"}`}>
              <ArrowUp size={15} /> Forward {forwardOn ? "ON" : "OFF"}
            </button>
            {forwardOn && (
              <label className="mt-2 flex items-center gap-2" data-testid="pv-fps-field">
                <span className="font-mono text-[8px] font-black uppercase tracking-widest text-white/60 shrink-0">Speed (fps)</span>
                <input value={fps} onChange={(e) => setFps(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))} inputMode="numeric" data-testid="pv-fps-input"
                  placeholder="65" className="flex-1 bg-[#141414] text-white placeholder:text-white/40 border-2 border-black px-3 py-2 font-mono text-[12px] outline-none focus:ring-2 focus:ring-[#FFD028]" />
              </label>
            )}
          </div>

          {/* 3 · Prompt box */}
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value.slice(0, 1000))} rows={3} data-testid="pv-prompt-input"
            placeholder="Describe the scene / extra action beats…"
            className="w-full bg-[#141414] text-white placeholder:text-white/40 border-2 border-black px-3 py-2 font-mono text-[11px] outline-none focus:ring-2 focus:ring-[#FFD028] resize-none" />

          <button onClick={saveWorkingPrompt} disabled={!prompt.trim()} data-testid="pv-save-prompt" className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#141414] text-white/70 py-2 font-mono text-[8px] font-black uppercase tracking-widest active:bg-[#FFD028] active:text-black disabled:opacity-40"><Save size={11} /> Save working prompt</button>

          {/* 4 · Duration */}
          <div>
            <p className="font-mono text-[9px] font-black uppercase tracking-widest text-[#FFD028] mb-1.5">Scene length</p>
            <div className="grid grid-cols-3 gap-2" data-testid="pv-durations">
              {DURS.map((d) => (
                <button key={d} onClick={() => setDuration(d)} data-testid={`pv-duration-${d}`}
                  className={`border-2 border-black py-2 font-mono text-[11px] font-black shadow-[2px_2px_0px_#000] ${duration === d ? "bg-[#FFD028] text-black" : "bg-[#141414] text-white/60"}`}>{d}s</button>
              ))}
            </div>
          </div>

          <button onClick={generate} disabled={generating || busy || !char?.file} data-testid="pv-generate"
            className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#FFD028] text-black py-3 font-mono text-[11px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
            {generating ? <Loader2 size={15} className="animate-spin" /> : <Film size={15} />} {generating ? "Rendering scene…" : "Generate Scene"}
          </button>
          <p className="font-mono text-[8px] text-white/40 text-center -mt-1">Holds 3 Credits (~$0.30) per AI scene · refunded if it fails</p>

          {/* Generating screen: Character placeholder or a play icon */}
          {generating && (
            <div className="absolute inset-0 z-20 bg-black/92 grid place-items-center p-4" data-testid="pv-generating">
              <div className="flex flex-col items-center gap-3">
                <div className="relative w-40 aspect-[9/16] border-2 border-[#FFD028] bg-black overflow-hidden shadow-[0_0_30px_rgba(255,208,40,0.35)]">
                  {char?.src ? <img src={char.src} alt="placeholder" className="w-full h-full object-cover opacity-70" data-testid="pv-generating-placeholder" />
                    : <div className="w-full h-full grid place-items-center" data-testid="pv-generating-play"><Play size={40} className="text-[#FFD028]" /></div>}
                  <div className="absolute inset-0 grid place-items-center bg-black/40"><Loader2 size={30} className="text-[#FFD028] animate-spin" /></div>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFD028]">Generating scene…</span>
              </div>
            </div>
          )}
        </div>

        {/* Result + save destinations (Device / Vault / Stock / Project) */}
        {lastAsset?.id && !generating && (
          <div className="border-2 border-[#FFD028] bg-[#0d0d0d] shadow-[4px_4px_0px_#000] p-3 space-y-3" data-testid="pv-result">
            <p className="font-mono text-[10px] font-black uppercase tracking-widest text-[#FFD028]">Scene ready</p>
            <div className="mx-auto w-40 aspect-[9/16] border-2 border-black bg-black overflow-hidden"><SceneVideo assetId={lastAsset.id} /></div>
            <div className="flex items-center gap-2 border-2 border-black bg-black/40 px-2 py-1.5">
              <span className="font-mono text-[8px] font-black uppercase tracking-widest text-[#00F5D4]">✓ Kept in this Project</span>
            </div>
            <SaveDestinations assetId={lastAsset.id} track="VIDEOS" album={project.title} filename={`${project.title}_scene`} ext="mp4" commit size="sm" />
            <div className="grid grid-cols-2 gap-2">
              <button onClick={clearInputs} data-testid="pv-next-scene" className="flex items-center justify-center gap-2 border-2 border-black bg-[#FFD028] text-black py-2.5 font-mono text-[10px] font-black uppercase tracking-widest shadow-[2px_2px_0px_#000]"><Plus size={13} /> Next Scene</button>
              <button onClick={fixLast} data-testid="pv-fix-scene" className="flex items-center justify-center gap-2 border-2 border-black bg-[#141414] text-white py-2.5 font-mono text-[10px] font-black uppercase tracking-widest"><RotateCcw size={13} /> Fix Last Scene</button>
            </div>
          </div>
        )}

        {/* Scene grid */}
        <div>
          <p className="font-mono text-[9px] font-black uppercase tracking-widest text-white/50 mb-2">Project Scenes</p>
          {loading ? <div className="grid place-items-center py-8"><Loader2 size={20} className="text-[#FFD028] animate-spin" /></div>
            : scenes.length === 0 ? <p className="font-mono text-[10px] text-white/40 border-2 border-dashed border-white/15 p-4 text-center">No scenes yet — generate your first scene above.</p>
              : <div className="grid grid-cols-3 gap-2" data-testid="pv-scene-grid">{scenes.map((s, i) => <SceneCard key={s.id} scene={s} index={i} onDelete={deleteScene} busy={busy} />)}</div>}
        </div>
      </div>

      {/* Prompt Album modal */}
      {folderOpen && (
        <div className="fixed inset-0 z-[80] bg-black/95 flex flex-col" data-testid="pv-folder-modal">
          <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b-2 border-black">
            <span className="flex items-center gap-2 font-mono text-[12px] font-black uppercase tracking-widest text-[#FFD028]"><FolderOpen size={16} /> Prompt Album</span>
            <button onClick={() => setFolderOpen(false)} data-testid="pv-folder-close" className="w-8 h-8 grid place-items-center border-2 border-black bg-[#141414] text-white"><X size={16} /></button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-3 space-y-2">
            {folderBusy ? <div className="grid place-items-center py-10"><Loader2 size={20} className="text-[#FFD028] animate-spin" /></div>
              : saved.length === 0 ? <p className="text-center font-mono text-[10px] text-white/40 py-10">No saved prompts yet — tap “Save working prompt” to add one.</p>
                : saved.map((p) => (
                  <div key={p.id} className="border-2 border-black bg-[#0d0d0d] p-3 flex items-start justify-between gap-2" data-testid={`pv-prompt-${p.id}`}>
                    <div className="min-w-0"><p className="font-mono text-[11px] font-black text-white truncate">{p.name}</p><p className="font-mono text-[8px] text-white/40 line-clamp-2 mt-0.5">{p.prompt_text}</p></div>
                    <button onClick={() => { setPrompt(p.prompt_text || ""); setFolderOpen(false); toast.success(`Loaded "${p.name}"`); }} data-testid={`pv-prompt-load-${p.id}`}
                      className="shrink-0 border-2 border-black bg-[#FFD028] text-black px-3 py-2 font-mono text-[9px] font-black uppercase tracking-widest">Load</button>
                  </div>
                ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ProjectVaultFlow({ onBack }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    try { const r = await projectsApi.list(); setProjects(r.projects || []); }
    catch { toast.error("Could not load projects"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try { const p = await projectsApi.create(title.trim() || "Untitled Project"); setTitle(""); setShowNew(false); await load(); setActive(p); }
    catch { toast.error("Could not create project"); }
    finally { setCreating(false); }
  };
  const removeProject = async (e, pid) => {
    e.stopPropagation();
    if (!window.confirm("Delete this project and all its scenes?")) return;
    try { await projectsApi.remove(pid); setProjects((p) => p.filter((x) => x.id !== pid)); }
    catch { toast.error("Delete failed"); }
  };

  if (active) return <ProjectWorkspace project={active} onBack={() => { setActive(null); load(); }} />;

  return (
    <div className="h-full w-full flex flex-col overflow-hidden" data-testid="projectvault-root">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-4">
        {onBack && (
          <button onClick={onBack} data-testid="projectvault-back-hub" className="flex items-center gap-1.5 border-2 border-black bg-[#141414] text-white px-3 py-2 font-mono text-[9px] font-black uppercase tracking-widest shadow-[2px_2px_0px_#000]"><ChevronLeft size={13} /> Director Hub</button>
        )}
        <p className="font-mono text-[10px] text-white/60 leading-relaxed">Name a project, then build it scene-by-scene — character + scene references, a movement macro and a working-prompt album. Join the clips later in the Video Joiner.</p>

        <button onClick={() => setShowNew(true)} data-testid="projectvault-new" className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#FFD028] text-black py-3 font-mono text-[11px] font-black uppercase tracking-widest shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none"><FolderPlus size={16} /> New Project</button>

        {loading ? <div className="grid place-items-center py-10"><Loader2 size={22} className="text-[#FFD028] animate-spin" /></div>
          : projects.length === 0 ? <p className="font-mono text-[10px] text-white/40 border-2 border-dashed border-white/15 p-5 text-center">No projects yet. Create one to start building scenes.</p>
            : (
              <div className="space-y-2.5">
                {projects.map((p) => (
                  <button key={p.id} onClick={() => setActive(p)} data-testid={`project-open-${p.id}`} className="w-full flex items-center gap-3 border-2 border-black bg-[#0d0d0d] p-3 text-left shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
                    <div className="w-14 h-14 border-2 border-black bg-black grid place-items-center shrink-0 overflow-hidden">{p.thumb_asset_id ? <SceneVideo assetId={p.thumb_asset_id} /> : <Clapperboard size={22} className="text-[#FFD028]" />}</div>
                    <div className="min-w-0 flex-1"><p className="font-serif text-lg text-white truncate leading-none">{p.title}</p><p className="font-mono text-[8px] uppercase tracking-widest text-white/40 mt-1.5">{p.scene_count} scene{p.scene_count === 1 ? "" : "s"}</p></div>
                    <span onClick={(e) => removeProject(e, p.id)} data-testid={`project-delete-${p.id}`} className="w-8 h-8 grid place-items-center border-2 border-black bg-[#141414] text-red-400 active:bg-red-500 active:text-white"><Trash2 size={13} /></span>
                  </button>
                ))}
              </div>
            )}
      </div>

      {showNew && (
        <div className="absolute inset-0 z-[100] bg-black/85 flex items-end" data-testid="projectvault-new-modal" onClick={() => setShowNew(false)} style={{ position: "fixed" }}>
          <div className="w-full bg-[#0d0d0d] border-t-4 border-[#FFD028] rounded-t-2xl p-4 pb-6" onClick={(e) => e.stopPropagation()}>
            <p className="font-mono text-xs font-black uppercase tracking-widest text-white mb-3">Name your project</p>
            <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, 80))} data-testid="projectvault-title-input" placeholder="e.g. Summer Road Trip" autoFocus
              className="w-full bg-[#141414] text-white placeholder:text-white/40 border-2 border-black px-3 py-3 font-mono text-sm outline-none focus:ring-2 focus:ring-[#FFD028] mb-3" />
            <button onClick={create} disabled={creating} data-testid="projectvault-create" className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#FFD028] text-black py-3 font-mono text-[11px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000] disabled:opacity-50">
              {creating ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create Project
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectVaultFlow;
